import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FaSyncAlt, FaDownload, FaCopy, FaPlus, FaMinus } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse';

const Mt5Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({
    type: 'mt5_deposit,mt5_withdrawal',
    status: '',
    login: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.login) params.append('login', filters.login);

      try {
        const response = await axios.get(
          `https://sys.instant-m-transfer.com/api/admin/mt5-transactions?${params.toString()}`
        );
        const sortedTxs = (response.data.transactions || []).sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        );
        setTransactions(sortedTxs);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch MT5 transactions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [filters]);

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        id: 'date',
        accessor: 'transactionDate',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          const date = new Date(value);
          return date.toLocaleDateString('en-US', {
            timeZone: 'Africa/Nairobi',
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
          });
        }
      },
      {
        Header: 'Time',
        id: 'time',
        accessor: 'transactionDate',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          const date = new Date(value);
          return date.toLocaleTimeString('en-US', {
            timeZone: 'Africa/Nairobi',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      },
      {
        Header: 'Type',
        accessor: 'transactionType',
        Cell: ({ value }) => (
          <span
            className={`px-1.5 py-0.5 rounded-full text-[6px] font-sfts-bold sm:text-xs ${
              value === 'mt5_deposit'
                ? 'bg-green-100 text-green-800'
                : 'bg-orange-100 text-orange-800'
            }`}
          >
            {value === 'mt5_deposit' ? 'To MT5' : 'From MT5'}
          </span>
        )
      },
      {
        Header: 'MT5 Login',
        accessor: 'mt5Login',
        Cell: ({ value }) => (
          <div className="flex items-center gap-1">
            <span className=" text-sm ">{value}</span>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
              }}
              className="text-gray-400 hover:text-gray-600"
              title="Copy login"
            >
              <FaCopy size={10} />
            </button>
          </div>
        )
      },
      { Header: 'Market', accessor: 'mt5AccountName', Cell: ({ value }) => value || '—' },
      {
        Header: 'CR Acct',
        accessor: 'accountReference',
        Cell: ({ value }) => <span className="  text-sm text-back">{value}</span>

      },
      {
        Header: 'USD',
        accessor: 'amountUSD',
        Cell: ({ value }) => <strong className="text-green-600 text-[7px] sm:text-sm">${value}</strong>
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <span
            className={`px-1.5 py-0.5 rounded-full text-[6px] font-sfts-bold sm:text-xs ${
              value === 'completed'
                ? 'bg-green-100 text-green-800'
                : value === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : value === 'failed'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value.toUpperCase()}
          </span>
        )
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: transactions,
      initialState: { pageSize: 20, sortBy: [{ id: 'date', desc: true }] }
    },
    useSortBy,
    usePagination
  );

  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRefresh = () => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);
    if (filters.login) params.append('login', filters.login);

    axios.get(`https://sys.instant-m-transfer.com/api/admin/mt5-transactions?${params.toString()}`)
      .then(res => {
        const sorted = (res.data.transactions || []).sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        );
        setTransactions(sorted);
      })
      .catch(() => setError('Failed to refresh'))
      .finally(() => setIsLoading(false));
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Time', 'Type', 'MT5 Login', 'Name', 'Market', 'CR Acct', 'USD', 'Status'];
    const rows = transactions.map(tx => {
      const date = new Date(tx.transactionDate);
      return [
        date.toLocaleDateString('en-US', { timeZone: 'Africa/Nairobi', year: '2-digit', month: '2-digit', day: '2-digit' }),
        date.toLocaleTimeString('en-US', { timeZone: 'Africa/Nairobi', hour: '2-digit', minute: '2-digit', hour12: false }),
        tx.transactionType === 'mt5_deposit' ? 'To MT5' : 'From MT5',
        tx.mt5Login,
        tx.mt5AccountName || '—',
        tx.mt5MarketType?.toUpperCase() || '—',
        tx.accountReference,
        tx.amountUSD,
        tx.status.toUpperCase()
      ];
    });

    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mt5_transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="bg-red-50 border-l-2 border-red-500 p-3 rounded-r shadow text-red-700 text-xs font-sftsMedium text-center">
        {error}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12">
        <LoadingImpulse size="lg" color="blue" />
      </div>
    );
  }

  if (!transactions.length) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="text-center text-gray-500">
        <span className="material-icons text-6xl text-gray-300 mb-2">account_balance</span>
        <p className="text-sm italic">No MT5 transactions found</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4">
      <div className="max-w-full mx-auto sm:max-w-3xl md:max-w-6xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h1 className="text-base font-sfts-bold text-gray-800 sm:text-xl md:text-2xl font-sfts-title">
            MT5 Transactions
          </h1>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-xs font-sftsMedium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-['Open_Sans']"
            >
              <FaDownload />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
            <button
              onClick={handleRefresh}
              className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-xs font-sftsMedium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-['Open_Sans']"
            >
              <FaSyncAlt className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <select
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-['Open_Sans']"
          >
            <option value="mt5_deposit,mt5_withdrawal">All MT5</option>
            <option value="mt5_deposit">Deposits Only</option>
            <option value="mt5_withdrawal">Withdrawals Only</option>
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-['Open_Sans']"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="text"
            name="login"
            placeholder="Search MT5 Login..."
            value={filters.login}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-['Open_Sans']"
          />
        </div>

        {/* Mobile View - Card Layout */}
        {isMobile ? (
          <div className="space-y-3">
            {page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.has(row.id);
              const rowData = row.original;

              return (
                <div key={row.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div 
                    className="p-3 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleRowExpansion(row.id)}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-sfts-bold ${
                          rowData.transactionType === 'mt5_deposit'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {rowData.transactionType === 'mt5_deposit' ? 'TO MT5' : 'FROM MT5'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-sfts-bold ${
                          rowData.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : rowData.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : rowData.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rowData.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-xs font-sfts-bold text-green-600">
                        ${rowData.amountUSD}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-[10px] text-gray-600 font-sftsMedium">
                          {rowData.mt5Login}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(rowData.mt5Login);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <FaCopy size={8} />
                        </button>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {new Date(rowData.transactionDate).toLocaleTimeString('en-US', {
                          timeZone: 'Africa/Nairobi',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })}
                      </div>
                    </div>
                    <button className="text-red-500">
                      {isExpanded ? <FaMinus size={12} /> : <FaPlus size={12} />}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-2 pt-3 text-[10px]">
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Date</div>
                          <div className="text-gray-800">
                            {new Date(rowData.transactionDate).toLocaleDateString('en-US', {
                              timeZone: 'Africa/Nairobi',
                              year: '2-digit',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Market</div>
                          <div className="text-gray-800">{rowData.mt5AccountName || '—'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">CR Account</div>
                          <div className="flex items-center gap-1">
                            <span className="font-sftsMedium text-blue-600">{rowData.accountReference}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(rowData.accountReference);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <FaCopy size={8} />
                            </button>
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Full Date</div>
                          <div className="text-gray-800 text-[9px]">
                            {new Date(rowData.transactionDate).toLocaleDateString('en-US', {
                              timeZone: 'Africa/Nairobi',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop View - Table Layout */
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="w-full">
                <thead className="bg-gray-800 text-white">
                  {headerGroups.map(headerGroup => {
                    const { key, ...props } = headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={key} {...props}>
                        {headerGroup.headers.map(column => {
                          const { key: colKey, ...colProps } = column.getHeaderProps(column.getSortByToggleProps());
                          return (
                            <th
                              key={colKey}
                              {...colProps}
                              className="p-0.25 text-left text-[6px] font-sfts-bold uppercase sm:p-2 sm:text-xs md:p-3 md:text-sm font-sfts-title min-w-[40px] sm:min-w-[80px]"
                            >
                              {column.render('Header')}
                              <span className="ml-0.5 sm:ml-1">
                                {column.isSorted ? (column.isSortedDesc ? ' Down' : ' Up') : ''}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                  {page.map(row => {
                    prepareRow(row);
                    const { key, ...rowProps } = row.getRowProps();
                    return (
                      <tr key={key} {...rowProps} className="hover:bg-gray-50 transition-colors duration-200">
                        {row.cells.map(cell => {
                          const { key: cellKey, ...cellProps } = cell.getCellProps();
                          return (
                            <td
                              key={cellKey}
                              {...cellProps}
                              className="p-0.25 text-[7px] text-gray-700 sm:p-2 sm:text-sm md:p-3 md:text-base font-['Open_Sans'] min-w-[40px] sm:min-w-[80px] truncate"
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pageOptions.length > 1 && (
            <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-1 sm:mt-3 sm:gap-2 md:mt-4 md:gap-3">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-sftsMedium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-['Open_Sans']"
          >
            Previous
          </button>
          <span className="text-xs text-gray-600 sm:text-sm md:text-base font-['Open_Sans']">
            Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-sftsMedium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-['Open_Sans']"
          >
            Next
          </button>
        </div>
        )}
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Open+Sans:wght@400&display=swap');
      `}</style>
    </div>
  );
};

export default Mt5Transactions;